import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, AlertCircle, Trash2, Eye } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ErrorLogs() {
  const [logs, setLogs] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/error-logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(response.data);
    } catch (error) {
      toast.error('Failed to load error logs');
    } finally {
      setLoading(false);
    }
  };

  // OPTIMIZATION: No need for extra API call, just show from logs array
  const viewDetail = (log) => {
    setSelectedLog(log);
    setShowDetail(true);
  };

  const clearLogs = async () => {
    if (!window.confirm('Clear all error logs? This cannot be undone.')) return;
    
    try {
      await axios.delete(`${API_URL}/error-logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Error logs cleared');
      setLogs([]);
    } catch (error) {
      toast.error('Failed to clear error logs');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getContextColor = useMemo(() => (context) => {
    const colors = {
      'sms_send': 'bg-red-100 text-red-800',
      'geocode': 'bg-orange-100 text-orange-800',
      'allocation': 'bg-yellow-100 text-yellow-800',
      'csv_upload': 'bg-blue-100 text-blue-800',
      'default': 'bg-slate-100 text-slate-800'
    };
    return colors[context] || colors.default;
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <p className="text-slate-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                Error Logs
              </h1>
              <p className="text-sm text-slate-600 mt-1">View detailed error history</p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}
                data-testid="back-to-dashboard"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
              <Button
                variant="destructive"
                onClick={clearLogs}
                disabled={logs.length === 0}
                data-testid="clear-logs-button"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear All Logs
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {logs.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <AlertCircle className="w-12 h-12 mx-auto text-slate-400 mb-4" />
              <p className="text-slate-600">No error logs found. Great job!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {logs.map((log) => (
              <Card key={log.id} className="hover:shadow-md transition-shadow" data-testid={`error-log-${log.id}`}>
                <CardContent className="py-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getContextColor(log.context)}`}>
                          {log.context || 'general'}
                        </span>
                        <span className="text-xs text-slate-500">
                          {formatDate(log.created_at)}
                        </span>
                      </div>
                      <p className="text-sm text-slate-800 font-medium mb-1">{log.message}</p>
                      {log.stack_or_details && (
                        <p className="text-xs text-slate-600 font-mono bg-slate-50 p-2 rounded mt-2 line-clamp-2">
                          {log.stack_or_details}
                        </p>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => viewDetail(log)}
                      data-testid={`view-detail-${log.id}`}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Detail Dialog */}
        <Dialog open={showDetail} onOpenChange={setShowDetail}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Error Log Details</DialogTitle>
            </DialogHeader>
            
            {selectedLog && (
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-semibold text-slate-700">Context</Label>
                  <p className="mt-1">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getContextColor(selectedLog.context)}`}>
                      {selectedLog.context || 'general'}
                    </span>
                  </p>
                </div>

                <div>
                  <Label className="text-sm font-semibold text-slate-700">Timestamp</Label>
                  <p className="text-sm text-slate-600 mt-1">{formatDate(selectedLog.created_at)}</p>
                </div>

                <div>
                  <Label className="text-sm font-semibold text-slate-700">Error Message</Label>
                  <p className="text-sm text-slate-800 mt-1 p-3 bg-red-50 border border-red-200 rounded">
                    {selectedLog.message}
                  </p>
                </div>

                {selectedLog.stack_or_details && (
                  <div>
                    <Label className="text-sm font-semibold text-slate-700">Stack Trace / Details</Label>
                    <pre className="text-xs text-slate-700 mt-1 p-3 bg-slate-50 border border-slate-200 rounded overflow-x-auto whitespace-pre-wrap">
                      {selectedLog.stack_or_details}
                    </pre>
                  </div>
                )}

                {selectedLog.id && (
                  <div>
                    <Label className="text-sm font-semibold text-slate-700">Error ID</Label>
                    <p className="text-xs text-slate-500 mt-1 font-mono">{selectedLog.id}</p>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>

      {/* Footer */}
      <footer className="fixed bottom-0 w-full py-4 text-center bg-white/80 backdrop-blur border-t border-slate-200">
        <a
          href="https://donate.stripe.com/5kQ6oH99f72ze6OcIK4ZG00"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-slate-400 hover:text-slate-600"
        >
          Support this project
        </a>
      </footer>
    </div>
  );
}

const Label = ({ children, className }) => (
  <label className={`block text-sm font-medium text-slate-700 ${className || ''}`}>
    {children}
  </label>
);
